import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { pushShareApiKey, getShareApiKey } from '../services/widgetBridge';
import { isAuthenticated } from '../services/auth';
import { API_BASE_URL } from '../utils/constants';
import api from '../services/api';

/**
 * Hook that auto-provisions an API key for the iOS Share Extension.
 *
 * On mount (when user is authenticated and on iOS native), checks if there's
 * already a share API key stored in App Group. If not, creates one via the API
 * and stores it in shared storage for the Share Extension to use.
 *
 * Only runs on iOS native platform. No-op on web.
 */
export const useShareExtension = () => {
  useEffect(() => {
    const provisionShareApiKey = async () => {
      if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios' || !isAuthenticated()) {
        return;
      }

      try {
        // Check if API key already exists in shared storage
        const result = await getShareApiKey();
        if (result?.apiKey && result.apiKey.length > 0) {
          return; // Already provisioned
        }

        // Create a new API key for the Share Extension
        const response = await api.post('/api/v1/api-keys', {
          name: 'iOS Share Extension',
          scopes: ['tasks:write', 'notes:write', 'bookmarks:write'],
        });

        // Store the API key in App Group shared storage
        await pushShareApiKey(response.data.api_key, API_BASE_URL);
      } catch (error) {
        console.warn('[useShareExtension] Failed to provision share API key:', error);
      }
    };

    // Delay to let the app finish loading (similar to useWidgetData)
    const timer = setTimeout(() => {
      provisionShareApiKey();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);
};

